/** @flow */

import React, { type ElementProps } from "react";
import { localize } from "redux-i18n";
import {
  IconButton as ActionButton,
  CheckboxHierarchyTree,
  Icons,
  TooltipDropdown,
  View,
  Text,
} from "@components";
import type { OrganizationStructure, ReportAreasField } from "@modules/reporting/reducer/types";
import "./report-areas.style.scss";
import classNames from "classnames";

type Props = $Exact<{
  editable: boolean,
  selected: Array<number>,
  excluded: Array<number>,
  className?: string,
  organizationStructure: OrganizationStructure,
  onTreeUpdate: Function => void,
  removeItem?: (from: ReportAreasField, id: number) => void,
  resetAreas: () => void,
  ...PropsFromLocalize,
}>;

export class ReportAreas extends React.Component<Props> {
  getNodeName = (id: number, tree: Object = this.props.organizationStructure): string => {
    if (tree.data.id === id) {
      return tree.data.label;
    }

    return tree.data
      ? tree.children.reduce((acc: string, c: *) => acc || this.getNodeName(id, c), "")
      : "";
  };

  itemValues = (items: Array<number>): Array<$Exact<{ id: number, label: string }>> =>
    items.map(id => ({
      id,
      label: this.getNodeName(id),
    }));

  get excludedItems() {
    return [
      ...this.props.organizationStructure.children,
      { data: this.props.organizationStructure.data || {}, children: [] },
    ].filter(item => this.props.excluded.some(id => item.data.id === id));
  }

  handleTreeUpdate = (selectedAreas: [{ id: number, excluded: [number] }]) => {
    const composedAreas = selectedAreas.reduce(
      (acc, selectedArea) => {
        acc.selected.push(selectedArea.id);
        acc.excluded.push(...selectedArea.excluded);
        return acc;
      },
      {
        selected: [],
        excluded: [],
      },
    );
    this.props.onTreeUpdate && this.props.onTreeUpdate(composedAreas);
  };

  handleRemoveItem = (from: ReportAreasField, id: number) => () => {
    this.props.removeItem && this.props.removeItem(from, id);
    let res = { selected: this.props.selected, excluded: this.props.excluded };
    res[from] = res[from].filter(arr => arr !== id);
    this.props.onTreeUpdate && this.props.onTreeUpdate(res);
  };

  render() {
    return (
      <View
        className={classNames("report-area__container", this.props.className)}
        direction="row"
        gap={4}
        flex={1}
      >
        <View className="report-area__tree gs-container" flex={1}>
          <CheckboxHierarchyTree
            tree={this.props.organizationStructure}
            excluded={this.props.excluded}
            selected={this.props.selected}
            expanded={[]}
            onChange={this.handleTreeUpdate}
            disabled={!this.props.editable}
            searchable={this.props.editable}
            searchPlaceholder={this.props.t("FIND_DEPARTMENT")}
            resetAreas={this.props.resetAreas}
          />
        </View>
        <View className="report-area__selected gs-container" flex={1}>
          <div className="area-block">
            <div className="area-title d-flex justify-content-start align-items-center">
              <TooltipDropdown
                description={this.props.t("REPORT_SELECTED_AREAS_DESCRIPTION")}
                id="report-selected-areas-description"
              >
                <h6>{`${this.props.t("INCLUDE")}:`}</h6>
              </TooltipDropdown>
            </div>
            {this.itemValues(this.props.selected).map(selected => (
              <View
                className="area-line"
                direction="row"
                justifyContent="between"
                key={selected.id}
              >
                <Text className="flex-1 ellipsis" text={selected.label} />
                {this.props.editable && (
                  <ActionButton
                    onClick={this.handleRemoveItem("selected", selected.id)}
                    icon={<Icons.CloseIcon />}
                  />
                )}
              </View>
            ))}
          </div>
          {this.props.excluded.length ? (
            <div className="area-block">
              <div className="area-title d-flex justify-content-start align-items-center">
                <TooltipDropdown
                  description={this.props.t("REPORT_EXCLUDED_AREAS_DESCRIPTION")}
                  id="report-excluded-areas-description"
                >
                  {`${this.props.t("EXCLUDE")}:`}
                </TooltipDropdown>
              </div>
              {this.itemValues(this.props.excluded).map(item => (
                <View
                  className="area-line"
                  direction="row"
                  justifyContent="between"
                  key={item.id}
                  data-id={item.id}
                >
                  <Text className="flex-1 ellipsis" text={item.label} />
                  {this.props.editable && (
                    <ActionButton
                      onClick={this.handleRemoveItem("excluded", item.id)}
                      icon={<Icons.CloseIcon />}
                    />
                  )}
                </View>
              ))}
            </div>
          ) : null}
        </View>
      </View>
    );
  }
}

export default localize()(ReportAreas);
